import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 비동기 조회 상태 훅.
 *
 * 기획서 Phase 1 요구사항인 loading / empty / error 3상태를
 * 화면마다 손으로 만들지 않도록 한 곳에 모은다.
 */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** 낙관적 갱신 등으로 화면 데이터를 직접 갈아끼울 때 */
  setData: (next: T) => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // fn 이 매 렌더 새로 만들어져도 재조회가 폭주하지 않도록 ref 에 담는다.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    fnRef
      .current()
      .then((r) => {
        if (alive) setData(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload, setData };
}

/**
 * 저장/삭제 등 사용자 행동용. 진행 중 중복 클릭을 막고 오류 메시지를 돌려준다.
 */
export function useAction(): {
  run: (fn: () => Promise<void>) => Promise<boolean>;
  busy: boolean;
  error: string | null;
  clearError: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (fn: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { run, busy, error, clearError: () => setError(null) };
}
